import { type CallhomeCamera } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import {
  callhomeCameraToCameraIdentifier,
  type PlayingCamera,
} from 'components/VideoPlayer/controller/PlayingCamerasController';
import React, { useEffect, useMemo, useRef } from 'react';
import { useControllerState } from 'solox';
import PlayerOverlayCommon from 'src/core/video/components/PlayerOverlayCommon';
import CameraPlayer from 'src/core/video/components/players/CameraPlayer';
import { BLANK_IMAGE, useCameraThumbnailAtTime } from 'src/hooks/camera/useCameraThumbnail';
import useDevices from 'src/hooks/devices/useDevices';
import { useDeviceIsLocal } from 'src/hooks/useDeviceIsLocal';
import { type VideoDiscoverySite } from 'utils/types';
import { usePlaybackController } from '../../controller/usePlaybackController';
import { useMotionSegments } from '../VideoHistogram/common/useMotionSegments';

/** The minimum amount of video to play before looping, in ms. */
const MIN_QUICK_PLAY_TIME = 5000;

interface QuickPlayerProps {
  /** The location to play video from. */
  location: VideoDiscoverySite;
  /** The camera to play video from. */
  camera: CallhomeCamera;
  /** The time to start playing video.  If omitted, video will play live. */
  start?: number;
  /**
   * If specified, when playback reaches this point we'll loop back to the start.
   * Ignored if start is omitted.
   */
  end?: number;
  /** Load VTT subtitles? */
  vtt?: boolean;
  /** Called when playback starts. */
  onPlaying?: () => void;
  isPaused?: boolean;
  /** segmentDurationMs is currently only used for videoSearch.
   *  If used for other purposes, it will affect useCameraThumbnail hook */
  segmentDurationMs?: number;
  /** segmentStartTime is currently only used for videoSearch.
   *  If used for other purposes, it will affect useCameraThumbnail hook */
  segmentStartTime?: number;
  /** posterOverride is used to override the poster image. */
  posterOverride?: string;
  /** posterStyle is used to override the poster style. */
  posterStyle?: React.CSSProperties;
  /** withOverlay is used to show the overlay. */
  withOverlay?: boolean;
}

/**
 * Quick player displays a video player for a camera and plays video.
 * This is used in places like when you hover over the thumbnail in an event,
 * or when you hover over a camera thumbnail in the camera selector; places
 * where there isn't going to be a full UI with pause/play controls, and
 * the user is never going to seek.
 */
const QuickPlayer: React.FC<QuickPlayerProps> = ({
  location,
  camera,
  start,
  end,
  onPlaying,
  vtt,
  isPaused = false,
  segmentDurationMs,
  segmentStartTime = Date.now(),
  posterOverride,
  posterStyle,
  withOverlay = true,
}) => {
  const user = useUserSession();
  const locationIsLocal = useDeviceIsLocal(camera.deviceId);
  const { devicesById } = useDevices();
  const segmentStartTimeRef = useRef(segmentStartTime);

  const playingCameras: PlayingCamera[] = useMemo(
    () => [
      {
        ...callhomeCameraToCameraIdentifier(camera),
        camera,
        location,
        device: devicesById[camera.deviceId],
      },
    ],
    [camera, location, devicesById],
  );
  const playingCamera = playingCameras[0];

  const controller = usePlaybackController(
    playingCameras,
    'sd', // Never play best quality for quick play
    start || segmentStartTimeRef.current,
    {
      vtt,
      quick: true,
      segmentDurationMs,
    },
  );

  const [motionSegmentsByCamera] = useMotionSegments({
    playingCameras,
    ifCached: false,
    start: start ? start - 30000 : segmentStartTimeRef.current - 30000,
    end: start ? start + 30000 : segmentStartTimeRef.current + 30000,
  });

  const poster = useCameraThumbnailAtTime(
    playingCamera,
    motionSegmentsByCamera,
    start || segmentStartTimeRef.current,
    !start,
  );

  const playerId = playingCamera.playerId;
  const playing = useControllerState(controller.state, () => controller.anyPlayersPlaying());
  const playerState = useControllerState(
    controller.state,
    (state) => state.byPlayerId[playerId]?.state,
  );

  // Call `onPlaying()` when we start playing video.
  const everPlaying = useRef(false);
  useEffect(() => {
    if (!everPlaying.current) {
      everPlaying.current = true;
      onPlaying?.();
    }
  }, [playing, onPlaying]);

  // If `time.end` was specified, then seek back to the start whenever the player
  // gets past that time.  Some events have an `end` which is before or equal to
  // the `start` - make sure we play at least MIN_QUICK_PLAY_TIME of video.
  const seekEnd = start && end ? Math.max(start + MIN_QUICK_PLAY_TIME, end) : undefined;
  useEffect(() => {
    if (start && seekEnd) {
      return controller.state.subscribe((state) => {
        if (state.desiredTime >= seekEnd) {
          controller.seekToTime(start);
        }
      });
    }
  }, [controller, start, seekEnd]);

  useEffect(() => {
    controller.togglePaused(isPaused);

    if (isPaused && start) {
      controller.seekToTime(start);
    }
  }, [controller, isPaused, start]);

  if (!playerState) {
    return null;
  }
  return (
    <>
      <CameraPlayer
        poster={posterOverride || poster || BLANK_IMAGE}
        posterStyle={posterStyle}
        playbackController={controller}
        playingCamera={playingCamera}
        locationIsLocal={locationIsLocal || false}
        user={user}
        isMuted
      />
      {withOverlay && (
        <PlayerOverlayCommon
          playbackController={controller}
          playingCamera={playingCamera}
          locationIsLocal={locationIsLocal || false}
        />
      )}
    </>
  );
};

export default React.memo(QuickPlayer);
